import { Component, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { VehiclePriceSheetService } from '../../../services/vehicle-price-sheet/vehicle-price-sheet.service';
import { VehiclePriceSheet } from '../../../shared/models/vehicle-price-sheet.interface';
import { DestructorComponent } from '../../../components/destructor/destructor.component';

declare var bootstrap: any;

@Component({
  selector: 'app-vh-price-sheet',
  templateUrl: './vh-price-sheet.component.html',
  styleUrls: ['./vh-price-sheet.component.scss']
})
export class VhPriceSheetComponent extends DestructorComponent implements OnInit, OnDestroy {

  @Output() priceSheetSaved = new EventEmitter<void>();

  priceSheetForm!: FormGroup;
  selectedVehicle: any = null;
  isLoading = false;
  isEditMode = false;
  existingPriceSheet: VehiclePriceSheet | null = null;
  formValueChangeSubscription?: Subscription;

  // Subtotal properties
  descriptionSubtotal = { actual: 0, estimated: 0, difference: 0 };
  auctionChargesSubtotal = { actual: 0, estimated: 0, difference: 0 };
  refundsSubtotal = { actual: 0, estimated: 0, difference: 0 };
  localTransportSubtotal = { actual: 0, estimated: 0, difference: 0 };
  yardCostSubtotal = { actual: 0, estimated: 0, difference: 0 };
  preExportInspectionSubtotal = { actual: 0, estimated: 0, difference: 0 };
  shippingCostsSubtotal = { actual: 0, estimated: 0, difference: 0 };
  airmailChargesSubtotal = { actual: 0, estimated: 0, difference: 0 };
  islInternalSubtotal = { actual: 0, estimated: 0, difference: 0 };
  bankChargesSubtotal = { actual: 0, estimated: 0, difference: 0 };
  extraChargesSubtotal = { actual: 0, estimated: 0, difference: 0 };

  // Grand totals
  grandTotals = {
    totalCost: { actual: 0, estimated: 0, difference: 0 },
    income: { actual: 0, estimated: 0, difference: 0 },
    refunds: { actual: 0, estimated: 0, difference: 0 },
    loss: { actual: 0, estimated: 0, difference: 0 }
  };

  constructor(
    private fb: FormBuilder,
    private _priceSheetService: VehiclePriceSheetService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.initializeForm();
    this.setupModalListener();
    this.setupFormValueChanges();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  private initializeForm(): void {
    this.priceSheetForm = this.fb.group({
      // Vehicle Info
      ref_number: ['', [Validators.required]],
      chassis_number: [''],
      auction_date: [''],
      auction_lot_number: [''],
      auction: [''],

      // Description Section - New Separate Fields
      actual_selling_price_usd: [0],
      actual_rate: [0],
      actual_actual_cost_jpy: [0],
      actual_sales_cost: [0],

      // Description Section - Auction Charges
      actual_auction_charges: [0],
      actual_auction_price: [0],
      actual_auc_price_tax_refundable: [false],
      actual_auc_price_tax_percentage: [10],
      actual_auction_price_tax: [0],
      actual_bid_charge: [0],
      actual_bid_charge_tax_refundable: [false],
      actual_bid_charge_tax_percentage: [10],
      actual_bid_price_tax: [0],
      actual_sold_charge_seiyaku: [0],
      actual_selling_charge_shuppin: [0],
      actual_re_auc_sold_price: [0],
      actual_recycling: [0],

      // Description Section - Estimated (all set to 0 as per requirement)
      estimated_selling_price_usd: [0],
      estimated_rate: [0],
      estimated_actual_cost_jpy: [0],
      estimated_sales_cost: [0],

      // Auction Charges - Estimated
      estimated_auction_charges: [0],
      estimated_auction_price: [0],
      estimated_auc_price_tax_refundable: [false],
      estimated_auc_price_tax_percentage: [10],
      estimated_auction_price_tax: [0],
      estimated_bid_charge: [0],
      estimated_bid_charge_tax_refundable: [false],
      estimated_bid_charge_tax_percentage: [10],
      estimated_bid_price_tax: [0],
      estimated_sold_charge_seiyaku: [0],
      estimated_selling_charge_shuppin: [0],
      estimated_re_auc_sold_price: [0],
      estimated_recycling: [0],

      // Refunds Section - Actual
      actual_recycle_fee_paid: [0],
      actual_recycle_fee_paid_refundable: [false],
      actual_automobile_tax: [0],
      actual_automobile_tax_refundable: [false],
      actual_consumption_tax: [0],
      actual_consumption_tax_refundable: [false],
      actual_consumption_tax_percentage: [10],
      actual_car_insurance: [0],
      actual_car_insurance_refundable: [false],
      actual_weight_tax: [0],
      actual_weight_tax_refundable: [false],
      actual_radiation_check_fee: [0],
      actual_radiation_check_fee_refundable: [false],
      actual_meijhenko_deposit: [0],
      actual_meijhenko_deposit_refundable: [false],

      // Refunds Section - Estimated
      estimated_recycle_fee_paid: [0],
      estimated_recycle_fee_paid_refundable: [false],
      estimated_automobile_tax: [0],
      estimated_automobile_tax_refundable: [false],
      estimated_consumption_tax: [0],
      estimated_consumption_tax_refundable: [false],
      estimated_consumption_tax_percentage: [10],
      estimated_car_insurance: [0],
      estimated_car_insurance_refundable: [false],
      estimated_weight_tax: [0],
      estimated_weight_tax_refundable: [false],
      estimated_radiation_check_fee: [0],
      estimated_radiation_check_fee_refundable: [false],
      estimated_meijhenko_deposit: [0],
      estimated_meijhenko_deposit_refundable: [false],

      // Local Transport Section - Actual
      actual_first_transport_charge: [0],
      actual_second_transport_charge: [0],
      actual_third_transport_charge: [0],
      actual_auction_penalty: [0],
      actual_additional_fee_rikuso_co: [0],
      actual_fuel_charge: [0],

      // Local Transport Section - Estimated
      estimated_first_transport_charge: [0],
      estimated_second_transport_charge: [0],
      estimated_third_transport_charge: [0],
      estimated_auction_penalty: [0],
      estimated_additional_fee_rikuso_co: [0],
      estimated_fuel_charge: [0],

      // Yard Cost Section - Actual
      actual_engine_number: [0],
      actual_custom_shipping_charge: [0],
      actual_yard_cut_penalty: [0],
      actual_photo_shooting_fee: [0],
      actual_yard_to_port_yokohama: [0],
      actual_cutting_ckd_fees: [0],
      actual_vehicle_repair: [0],
      actual_cleaning_fee: [0],
      actual_inner_cargo_charge: [0],
      actual_yard_storage_fee: [0],
      actual_vanning: [0],
      actual_parts_disposal_fee: [0],
      actual_gas_removal_charge: [0],
      actual_additional_inspection_fee: [0],
      actual_mileage_repair: [0],
      actual_parts: [0],

      // Yard Cost Section - Estimated
      estimated_engine_number: [0],
      estimated_custom_shipping_charge: [0],
      estimated_yard_cut_penalty: [0],
      estimated_photo_shooting_fee: [0],
      estimated_yard_to_port_yokohama: [0],
      estimated_cutting_ckd_fees: [0],
      estimated_vehicle_repair: [0],
      estimated_cleaning_fee: [0],
      estimated_inner_cargo_charge: [0],
      estimated_yard_storage_fee: [0],
      estimated_vanning: [0],
      estimated_parts_disposal_fee: [0],
      estimated_gas_removal_charge: [0],
      estimated_additional_inspection_fee: [0],
      estimated_mileage_repair: [0],
      estimated_parts: [0],

      // Pre-Export Inspection Section - Actual
      actual_pre_export_inspection: [0],
      actual_jevic: [0],
      actual_eaa: [0],
      actual_ati_psi: [0],
      actual_mb_insp_cancel_fee: [0],
      actual_osij: [0],
      actual_cat_fee: [0],
      actual_heat_treatment_fuj: [0],
      actual_jam: [0],

      // Pre-Export Inspection Section - Estimated
      estimated_pre_export_inspection: [0],
      estimated_jevic: [0],
      estimated_eaa: [0],
      estimated_ati_psi: [0],
      estimated_mb_insp_cancel_fee: [0],
      estimated_osij: [0],
      estimated_cat_fee: [0],
      estimated_heat_treatment_fuj: [0],
      estimated_jam: [0],

      // Shipping Costs Section - Actual
      actual_bl_charges: [0],
      actual_shipping_extra_costs: [0],
      actual_ocean_freight: [0],
      actual_cargo_ctn: [0],
      actual_thc: [0],
      actual_seal_fee: [0],
      actual_doc_fee: [0],
      actual_booking_and_commission: [0],
      actual_surrender_fee: [0],
      actual_bl_docs_fee: [0],
      actual_freight_rate: [0],

      // Shipping Costs Section - Estimated
      estimated_bl_charges: [0],
      estimated_shipping_extra_costs: [0],
      estimated_ocean_freight: [0],
      estimated_cargo_ctn: [0],
      estimated_thc: [0],
      estimated_seal_fee: [0],
      estimated_doc_fee: [0],
      estimated_booking_and_commission: [0],
      estimated_surrender_fee: [0],
      estimated_bl_docs_fee: [0],
      estimated_freight_rate: [0],

      // Airmail Charges Section - Actual
      actual_dhl_docs_fee: [0],
      actual_fedex: [0],
      actual_ems: [0],

      // Airmail Charges Section - Estimated
      estimated_dhl_docs_fee: [0],
      estimated_fedex: [0],
      estimated_ems: [0],

      // ISL Internal Section - Actual
      actual_sales_commission: [0],
      actual_marine_insurance: [0],

      // ISL Internal Section - Estimated
      estimated_sales_commission: [0],
      estimated_marine_insurance: [0],

      // Bank Charges Section - Actual
      actual_lc_amendment_fee: [0],
      actual_collection_fee: [0],
      actual_stamp_fee: [0],
      actual_yen_exchange: [0],
      actual_issuing_bank_fee: [0],
      actual_transfer_fee: [0],
      actual_lc_cable_charge: [0],

      // Bank Charges Section - Estimated
      estimated_lc_amendment_fee: [0],
      estimated_collection_fee: [0],
      estimated_stamp_fee: [0],
      estimated_yen_exchange: [0],
      estimated_issuing_bank_fee: [0],
      estimated_transfer_fee: [0],
      estimated_lc_cable_charge: [0],

      // Extra Charges Section - Actual
      actual_extra_parts: [0],

      // Extra Charges Section - Estimated
      estimated_extra_parts: [0]
    });
  }

  private getVehiclePriceSheet(vehicle_id: string) {

    this.subscriptions.push(
      this._priceSheetService.GetData("1", "", { vehicle_id }).subscribe({
        next: (response: any) => {
          if (response.status && response.data?.data && response.data?.data.length) {
            this.existingPriceSheet = response.data.data[0];
            this.isEditMode = true;
            if (this.existingPriceSheet) {
              this.populateForm(this.existingPriceSheet);
            }
          } else {
            this.isEditMode = false;
            this.existingPriceSheet = null;
          }
        }
      })
    )

  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_vh_price_sheet');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const vehicle = (window as any).selectedVehicleForPriceSheet;
        if (vehicle) {
          this.selectedVehicle = vehicle;
          this.loadVehiclePriceSheet();
          this.populateVehicleInfo();
        } else {
          this.resetForm();
        }
        // Ensure calculations are triggered when modal is shown
        setTimeout(() => {
          this.calculateAuctionPriceTax();
          this.calculateBidChargeTax();
          this.calculateAllSubtotals();
          this.calculateGrandTotals();
        }, 200);
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this.resetForm();
        this.selectedVehicle = null;
        this.existingPriceSheet = null;
        this.isEditMode = false;
      });
    }
  }

  private loadVehiclePriceSheet(): void {
    if (this.selectedVehicle && this.selectedVehicle._id) {
      this.subscriptions.push(
        this._priceSheetService.GetDataByVehicleId(this.selectedVehicle._id).subscribe({
          next: (response: any) => {
            if (response.status && response.data) {
              this.existingPriceSheet = response.data;
              this.isEditMode = true;
              if (this.existingPriceSheet) {
                this.populateForm(this.existingPriceSheet);
              }
            } else {
              this.isEditMode = false;
              this.existingPriceSheet = null;
            }
          },
          error: (error) => {
            console.error('Error loading price sheet:', error);
            this.isEditMode = false;
            this.existingPriceSheet = null;
          }
        })
      );
    }
  }

  private populateVehicleInfo(): void {
    if (this.selectedVehicle) {
      this.priceSheetForm.patchValue({
        ref_number: this.selectedVehicle.inventory_no || this.selectedVehicle.car_no || '',
        chassis_number: this.selectedVehicle.vin_no || this.selectedVehicle.chassis_no || '',
        auction_date: this.selectedVehicle.auction_date || '',
        auction_lot_number: this.selectedVehicle.lot_no || '',
        auction: this.selectedVehicle.auction_hall || ''
      });
    }
  }

  private populateForm(priceSheet: any): void {
    // Map the organized section data back to individual form controls
    const formData: any = {};

    // Description Section - New Separate Fields
    if (priceSheet.description) {
      // Map the new separate fields
      formData.actual_selling_price_usd = priceSheet.description.selling_price_usd?.actual || 0;
      formData.estimated_selling_price_usd = priceSheet.description.selling_price_usd?.estimated || 0;
      formData.actual_rate = priceSheet.description.rate?.actual || 0;
      formData.estimated_rate = priceSheet.description.rate?.estimated || 0;
      formData.actual_actual_cost_jpy = priceSheet.description.actual_cost_jpy?.actual || 0;
      formData.estimated_actual_cost_jpy = priceSheet.description.actual_cost_jpy?.estimated || 0;
      formData.actual_sales_cost = priceSheet.description.sales_cost?.actual || 0;
      formData.estimated_sales_cost = priceSheet.description.sales_cost?.estimated || 0;

      // Auction charges
      formData.actual_auction_charges = priceSheet.description.auction_charges?.actual || 0;
      formData.estimated_auction_charges = priceSheet.description.auction_charges?.estimated || 0;
      formData.actual_auction_charges_percentage = priceSheet.description.auction_charges_percentage || 0;
      formData.actual_auction_price = priceSheet.description.auction_price?.actual || 0;
      formData.estimated_auction_price = priceSheet.description.auction_price?.estimated || 0;
      formData.actual_auction_price_tax = priceSheet.description.auction_price_tax?.actual || 0;
      formData.estimated_auction_price_tax = priceSheet.description.auction_price_tax?.estimated || 0;
      formData.actual_auc_price_tax_refundable = priceSheet.description.auc_price_tax_refundable || false;
      formData.actual_auc_price_tax_percentage = priceSheet.description.auc_price_tax_percentage || 0;
      formData.actual_bid_charge = priceSheet.description.bid_charge?.actual || 0;
      formData.estimated_bid_charge = priceSheet.description.bid_charge?.estimated || 0;
      formData.actual_bid_price_tax = priceSheet.description.bid_price_tax?.actual || 0;
      formData.estimated_bid_price_tax = priceSheet.description.bid_price_tax?.estimated || 0;
      formData.actual_bid_charge_tax_refundable = priceSheet.description.bid_charge_tax_refundable || false;
      formData.actual_bid_charge_tax_percentage = priceSheet.description.bid_charge_tax_percentage || 0;
      formData.actual_sold_charge_seiyaku = priceSheet.description.sold_charge_seiyaku?.actual || 0;
      formData.estimated_sold_charge_seiyaku = priceSheet.description.sold_charge_seiyaku?.estimated || 0;
      formData.actual_selling_charge_shuppin = priceSheet.description.selling_charge_shuppin?.actual || 0;
      formData.estimated_selling_charge_shuppin = priceSheet.description.selling_charge_shuppin?.estimated || 0;
      formData.actual_re_auc_sold_price = priceSheet.description.re_auc_sold_price?.actual || 0;
      formData.estimated_re_auc_sold_price = priceSheet.description.re_auc_sold_price?.estimated || 0;
      formData.actual_recycling = priceSheet.description.recycling?.actual || 0;
      formData.estimated_recycling = priceSheet.description.recycling?.estimated || 0;
    }

    // Refunds Section
    if (priceSheet.refunds) {
      formData.actual_recycle_fee_paid = priceSheet.refunds.recycle_fee_paid?.actual || 0;
      formData.estimated_recycle_fee_paid = priceSheet.refunds.recycle_fee_paid?.estimated || 0;
      formData.actual_recycle_fee_paid_refundable = priceSheet.refunds.recycle_fee_paid?.refundable || false;
      formData.actual_automobile_tax = priceSheet.refunds.automobile_tax?.actual || 0;
      formData.estimated_automobile_tax = priceSheet.refunds.automobile_tax?.estimated || 0;
      formData.actual_automobile_tax_refundable = priceSheet.refunds.automobile_tax?.refundable || false;
      formData.actual_consumption_tax = priceSheet.refunds.consumption_tax?.actual || 0;
      formData.estimated_consumption_tax = priceSheet.refunds.consumption_tax?.estimated || 0;
      formData.actual_consumption_tax_refundable = priceSheet.refunds.consumption_tax?.refundable || false;
      formData.actual_consumption_tax_percentage = priceSheet.refunds.consumption_tax?.percentage || 10;
      formData.actual_car_insurance = priceSheet.refunds.car_insurance?.actual || 0;
      formData.estimated_car_insurance = priceSheet.refunds.car_insurance?.estimated || 0;
      formData.actual_car_insurance_refundable = priceSheet.refunds.car_insurance?.refundable || false;
      formData.actual_weight_tax = priceSheet.refunds.weight_tax?.actual || 0;
      formData.estimated_weight_tax = priceSheet.refunds.weight_tax?.estimated || 0;
      formData.actual_weight_tax_refundable = priceSheet.refunds.weight_tax?.refundable || false;
      formData.actual_radiation_check_fee = priceSheet.refunds.radiation_check_fee?.actual || 0;
      formData.estimated_radiation_check_fee = priceSheet.refunds.radiation_check_fee?.estimated || 0;
      formData.actual_radiation_check_fee_refundable = priceSheet.refunds.radiation_check_fee?.refundable || false;
      formData.actual_meijhenko_deposit = priceSheet.refunds.meijhenko_deposit?.actual || 0;
      formData.estimated_meijhenko_deposit = priceSheet.refunds.meijhenko_deposit?.estimated || 0;
      formData.actual_meijhenko_deposit_refundable = priceSheet.refunds.meijhenko_deposit?.refundable || false;
    }

    // Local Transport Rikuso Section
    if (priceSheet.local_transport_rikuso) {
      formData.actual_first_transport_charge = priceSheet.local_transport_rikuso.first_transport_charge?.actual || 0;
      formData.estimated_first_transport_charge = priceSheet.local_transport_rikuso.first_transport_charge?.estimated || 0;
      formData.actual_second_transport_charge = priceSheet.local_transport_rikuso.second_transport_charge?.actual || 0;
      formData.estimated_second_transport_charge = priceSheet.local_transport_rikuso.second_transport_charge?.estimated || 0;
      formData.actual_third_transport_charge = priceSheet.local_transport_rikuso.third_transport_charge?.actual || 0;
      formData.estimated_third_transport_charge = priceSheet.local_transport_rikuso.third_transport_charge?.estimated || 0;
      formData.actual_auction_penalty = priceSheet.local_transport_rikuso.auction_penalty?.actual || 0;
      formData.estimated_auction_penalty = priceSheet.local_transport_rikuso.auction_penalty?.estimated || 0;
      formData.actual_additional_fee_rikuso_co = priceSheet.local_transport_rikuso.additional_fee_rikuso_co?.actual || 0;
      formData.estimated_additional_fee_rikuso_co = priceSheet.local_transport_rikuso.additional_fee_rikuso_co?.estimated || 0;
      formData.actual_fuel_charge = priceSheet.local_transport_rikuso.fuel_charge?.actual || 0;
      formData.estimated_fuel_charge = priceSheet.local_transport_rikuso.fuel_charge?.estimated || 0;
    }

    // Yard Cost Section
    if (priceSheet.yard_cost) {
      formData.actual_engine_number = priceSheet.yard_cost.engine_number?.actual || 0;
      formData.estimated_engine_number = priceSheet.yard_cost.engine_number?.estimated || 0;
      formData.actual_custom_shipping_charge = priceSheet.yard_cost.custom_shipping_charge?.actual || 0;
      formData.estimated_custom_shipping_charge = priceSheet.yard_cost.custom_shipping_charge?.estimated || 0;
      formData.actual_yard_cut_penalty = priceSheet.yard_cost.yard_cut_penalty?.actual || 0;
      formData.estimated_yard_cut_penalty = priceSheet.yard_cost.yard_cut_penalty?.estimated || 0;
      formData.actual_photo_shooting_fee = priceSheet.yard_cost.photo_shooting_fee?.actual || 0;
      formData.estimated_photo_shooting_fee = priceSheet.yard_cost.photo_shooting_fee?.estimated || 0;
      formData.actual_yard_to_port_yokohama = priceSheet.yard_cost.yard_to_port_yokohama?.actual || 0;
      formData.estimated_yard_to_port_yokohama = priceSheet.yard_cost.yard_to_port_yokohama?.estimated || 0;
      formData.actual_cutting_ckd_fees = priceSheet.yard_cost.cutting_ckd_fees?.actual || 0;
      formData.estimated_cutting_ckd_fees = priceSheet.yard_cost.cutting_ckd_fees?.estimated || 0;
      formData.actual_vehicle_repair = priceSheet.yard_cost.vehicle_repair?.actual || 0;
      formData.estimated_vehicle_repair = priceSheet.yard_cost.vehicle_repair?.estimated || 0;
      formData.actual_cleaning_fee = priceSheet.yard_cost.cleaning_fee?.actual || 0;
      formData.estimated_cleaning_fee = priceSheet.yard_cost.cleaning_fee?.estimated || 0;
      formData.actual_inner_cargo_charge = priceSheet.yard_cost.inner_cargo_charge?.actual || 0;
      formData.estimated_inner_cargo_charge = priceSheet.yard_cost.inner_cargo_charge?.estimated || 0;
      formData.actual_yard_storage_fee = priceSheet.yard_cost.yard_storage_fee?.actual || 0;
      formData.estimated_yard_storage_fee = priceSheet.yard_cost.yard_storage_fee?.estimated || 0;
      formData.actual_vanning = priceSheet.yard_cost.vanning?.actual || 0;
      formData.estimated_vanning = priceSheet.yard_cost.vanning?.estimated || 0;
      formData.actual_parts_disposal_fee = priceSheet.yard_cost.parts_disposal_fee?.actual || 0;
      formData.estimated_parts_disposal_fee = priceSheet.yard_cost.parts_disposal_fee?.estimated || 0;
      formData.actual_gas_removal_charge = priceSheet.yard_cost.gas_removal_charge?.actual || 0;
      formData.estimated_gas_removal_charge = priceSheet.yard_cost.gas_removal_charge?.estimated || 0;
      formData.actual_additional_inspection_fee = priceSheet.yard_cost.additional_inspection_fee?.actual || 0;
      formData.estimated_additional_inspection_fee = priceSheet.yard_cost.additional_inspection_fee?.estimated || 0;
      formData.actual_mileage_repair = priceSheet.yard_cost.mileage_repair?.actual || 0;
      formData.estimated_mileage_repair = priceSheet.yard_cost.mileage_repair?.estimated || 0;
      formData.actual_parts = priceSheet.yard_cost.parts?.actual || 0;
      formData.estimated_parts = priceSheet.yard_cost.parts?.estimated || 0;
    }

    // Pre-Export Inspection Section
    if (priceSheet.pre_export_inspection) {
      formData.actual_pre_export_inspection = priceSheet.pre_export_inspection.pre_export_inspection?.actual || 0;
      formData.estimated_pre_export_inspection = priceSheet.pre_export_inspection.pre_export_inspection?.estimated || 0;
      formData.actual_jevic = priceSheet.pre_export_inspection.jevic?.actual || 0;
      formData.estimated_jevic = priceSheet.pre_export_inspection.jevic?.estimated || 0;
      formData.actual_eaa = priceSheet.pre_export_inspection.eaa?.actual || 0;
      formData.estimated_eaa = priceSheet.pre_export_inspection.eaa?.estimated || 0;
      formData.actual_ati_psi = priceSheet.pre_export_inspection.ati_psi?.actual || 0;
      formData.estimated_ati_psi = priceSheet.pre_export_inspection.ati_psi?.estimated || 0;
      formData.actual_mb_insp_cancel_fee = priceSheet.pre_export_inspection.mb_insp_cancel_fee?.actual || 0;
      formData.estimated_mb_insp_cancel_fee = priceSheet.pre_export_inspection.mb_insp_cancel_fee?.estimated || 0;
      formData.actual_osij = priceSheet.pre_export_inspection.osij?.actual || 0;
      formData.estimated_osij = priceSheet.pre_export_inspection.osij?.estimated || 0;
      formData.actual_cat_fee = priceSheet.pre_export_inspection.cat_fee?.actual || 0;
      formData.estimated_cat_fee = priceSheet.pre_export_inspection.cat_fee?.estimated || 0;
      formData.actual_heat_treatment_fuj = priceSheet.pre_export_inspection.heat_treatment_fuj?.actual || 0;
      formData.estimated_heat_treatment_fuj = priceSheet.pre_export_inspection.heat_treatment_fuj?.estimated || 0;
      formData.actual_jam = priceSheet.pre_export_inspection.jam?.actual || 0;
      formData.estimated_jam = priceSheet.pre_export_inspection.jam?.estimated || 0;
    }

    // Shipping Costs Section
    if (priceSheet.shipping_costs) {
      formData.actual_bl_charges = priceSheet.shipping_costs.bl_charges?.actual || 0;
      formData.estimated_bl_charges = priceSheet.shipping_costs.bl_charges?.estimated || 0;
      formData.actual_shipping_extra_costs = priceSheet.shipping_costs.shipping_extra_costs?.actual || 0;
      formData.estimated_shipping_extra_costs = priceSheet.shipping_costs.shipping_extra_costs?.estimated || 0;
      formData.actual_ocean_freight = priceSheet.shipping_costs.ocean_freight?.actual || 0;
      formData.estimated_ocean_freight = priceSheet.shipping_costs.ocean_freight?.estimated || 0;
      formData.actual_cargo_ctn = priceSheet.shipping_costs.cargo_ctn?.actual || 0;
      formData.estimated_cargo_ctn = priceSheet.shipping_costs.cargo_ctn?.estimated || 0;
      formData.actual_thc = priceSheet.shipping_costs.thc?.actual || 0;
      formData.estimated_thc = priceSheet.shipping_costs.thc?.estimated || 0;
      formData.actual_seal_fee = priceSheet.shipping_costs.seal_fee?.actual || 0;
      formData.estimated_seal_fee = priceSheet.shipping_costs.seal_fee?.estimated || 0;
      formData.actual_doc_fee = priceSheet.shipping_costs.doc_fee?.actual || 0;
      formData.estimated_doc_fee = priceSheet.shipping_costs.doc_fee?.estimated || 0;
      formData.actual_booking_and_commission = priceSheet.shipping_costs.booking_and_commission?.actual || 0;
      formData.estimated_booking_and_commission = priceSheet.shipping_costs.booking_and_commission?.estimated || 0;
      formData.actual_surrender_fee = priceSheet.shipping_costs.surrender_fee?.actual || 0;
      formData.estimated_surrender_fee = priceSheet.shipping_costs.surrender_fee?.estimated || 0;
      formData.actual_bl_docs_fee = priceSheet.shipping_costs.bl_docs_fee?.actual || 0;
      formData.estimated_bl_docs_fee = priceSheet.shipping_costs.bl_docs_fee?.estimated || 0;
      formData.actual_freight_rate = priceSheet.shipping_costs.freight_rate?.actual || 0;
      formData.estimated_freight_rate = priceSheet.shipping_costs.freight_rate?.estimated || 0;
    }

    // Airmail Charges Section
    if (priceSheet.airmail_charges) {
      formData.actual_dhl_docs_fee = priceSheet.airmail_charges.dhl_docs_fee?.actual || 0;
      formData.estimated_dhl_docs_fee = priceSheet.airmail_charges.dhl_docs_fee?.estimated || 0;
      formData.actual_fedex = priceSheet.airmail_charges.fedex?.actual || 0;
      formData.estimated_fedex = priceSheet.airmail_charges.fedex?.estimated || 0;
      formData.actual_ems = priceSheet.airmail_charges.ems?.actual || 0;
      formData.estimated_ems = priceSheet.airmail_charges.ems?.estimated || 0;
    }

    // ISL Internal Section
    if (priceSheet.isl_internal) {
      formData.actual_sales_commission = priceSheet.isl_internal.sales_commission?.actual || 0;
      formData.estimated_sales_commission = priceSheet.isl_internal.sales_commission?.estimated || 0;
      formData.actual_marine_insurance = priceSheet.isl_internal.marine_insurance?.actual || 0;
      formData.estimated_marine_insurance = priceSheet.isl_internal.marine_insurance?.estimated || 0;
    }

    // Bank Charges Section
    if (priceSheet.bank_charges) {
      formData.actual_lc_amendment_fee = priceSheet.bank_charges.lc_amendment_fee?.actual || 0;
      formData.estimated_lc_amendment_fee = priceSheet.bank_charges.lc_amendment_fee?.estimated || 0;
      formData.actual_collection_fee = priceSheet.bank_charges.collection_fee?.actual || 0;
      formData.estimated_collection_fee = priceSheet.bank_charges.collection_fee?.estimated || 0;
      formData.actual_stamp_fee = priceSheet.bank_charges.stamp_fee?.actual || 0;
      formData.estimated_stamp_fee = priceSheet.bank_charges.stamp_fee?.estimated || 0;
      formData.actual_yen_exchange = priceSheet.bank_charges.yen_exchange?.actual || 0;
      formData.estimated_yen_exchange = priceSheet.bank_charges.yen_exchange?.estimated || 0;
      formData.actual_issuing_bank_fee = priceSheet.bank_charges.issuing_bank_fee?.actual || 0;
      formData.estimated_issuing_bank_fee = priceSheet.bank_charges.issuing_bank_fee?.estimated || 0;
      formData.actual_transfer_fee = priceSheet.bank_charges.transfer_fee?.actual || 0;
      formData.estimated_transfer_fee = priceSheet.bank_charges.transfer_fee?.estimated || 0;
      formData.actual_lc_cable_charge = priceSheet.bank_charges.lc_cable_charge?.actual || 0;
      formData.estimated_lc_cable_charge = priceSheet.bank_charges.lc_cable_charge?.estimated || 0;
    }

    // Extra Charges Section
    if (priceSheet.extra_charges) {
      formData.actual_extra_parts = priceSheet.extra_charges.extra_parts?.actual || 0;
      formData.estimated_extra_parts = priceSheet.extra_charges.extra_parts?.estimated || 0;
    }

    // Apply the form data
    this.priceSheetForm.patchValue(formData);

    // Trigger calculations after populating form
    setTimeout(() => {
      this.calculateAuctionPriceTax();
      this.calculateBidChargeTax();
      this.calculateAllSubtotals();
      this.calculateGrandTotals();
    }, 100);
  }

  private resetForm(): void {
    this.priceSheetForm.reset();
    this.initializeForm();
    // Re-setup form value changes after form reset
    this.setupFormValueChanges();
  }

  onSubmit(): void {
    if (this.priceSheetForm.valid && this.selectedVehicle) {
      this.isLoading = true;
      const formData = this.preparePriceSheetData();

      // Log the organized data structure
      console.log('=== ORGANIZED FORM DATA BY SECTIONS ===');
      console.log('Individual field values:', formData);

      // If you need subtotals calculated, you can use this method:
      const dataWithSubtotals = this.getCompleteDataWithSubtotals();
      console.log('=== DATA WITH CALCULATED SUBTOTALS ===');
      console.log('Data with subtotals:', dataWithSubtotals);

      const operation = this.isEditMode && this.existingPriceSheet
        ? this._priceSheetService.UpdateData(this.existingPriceSheet._id!, formData)
        : this._priceSheetService.AddData(formData);

      this.subscriptions.push(
        operation.subscribe({
          next: (response: any) => {
            if (response.status) {
              Swal.fire({
                title: 'Success!',
                text: `Price sheet ${this.isEditMode ? 'updated' : 'created'} successfully`,
                icon: 'success',
                confirmButtonText: 'OK'
              });
              this.priceSheetSaved.emit();
              this.closeModal();
            } else {
              Swal.fire({
                title: 'Error!',
                text: response.message || `Failed to ${this.isEditMode ? 'update' : 'create'} price sheet`,
                icon: 'error',
                confirmButtonText: 'OK'
              });
            }
            this.isLoading = false;
          },
          error: (error) => {
            console.error('Error saving price sheet:', error);
            Swal.fire({
              title: 'Error!',
              text: `Failed to ${this.isEditMode ? 'update' : 'create'} price sheet`,
              icon: 'error',
              confirmButtonText: 'OK'
            });
            this.isLoading = false;
          }
        })
      );
    }
  }

  private preparePriceSheetData(): any {
    const formValue = this.priceSheetForm.value;

    return {
      // Basic vehicle information
      vehicle_id: this.selectedVehicle._id,

      // Organized by sections with individual field values
      description: {
        selling_price_usd: {
          actual: formValue.actual_selling_price_usd || 0,
          estimated: formValue.estimated_selling_price_usd || 0
        },
        rate: {
          actual: formValue.actual_rate || 0,
          estimated: formValue.estimated_rate || 0
        },
        actual_cost_jpy: {
          actual: formValue.actual_actual_cost_jpy || 0,
          estimated: formValue.estimated_actual_cost_jpy || 0
        },
        sales_cost: {
          actual: formValue.actual_sales_cost || 0,
          estimated: formValue.estimated_sales_cost || 0
        },
        auction_charges: {
          actual: formValue.actual_auction_charges || 0,
          estimated: formValue.estimated_auction_charges || 0
        },
        auction_charges_percentage: formValue.actual_auction_charges_percentage || 0,
        auction_price: {
          actual: formValue.actual_auction_price || 0,
          estimated: formValue.estimated_auction_price || 0
        },
        auction_price_tax: {
          actual: formValue.actual_auction_price_tax || 0,
          estimated: formValue.estimated_auction_price_tax || 0
        },
        auc_price_tax_refundable: formValue.actual_auc_price_tax_refundable || false,
        auc_price_tax_percentage: formValue.actual_auc_price_tax_percentage || 0,
        bid_charge: {
          actual: formValue.actual_bid_charge || 0,
          estimated: formValue.estimated_bid_charge || 0
        },
        bid_price_tax: {
          actual: formValue.actual_bid_price_tax || 0,
          estimated: formValue.estimated_bid_price_tax || 0
        },
        bid_charge_tax_refundable: formValue.actual_bid_charge_tax_refundable || false,
        bid_charge_tax_percentage: formValue.actual_bid_charge_tax_percentage || 0,
        sold_charge_seiyaku: {
          actual: formValue.actual_sold_charge_seiyaku || 0,
          estimated: formValue.estimated_sold_charge_seiyaku || 0
        },
        selling_charge_shuppin: {
          actual: formValue.actual_selling_charge_shuppin || 0,
          estimated: formValue.estimated_selling_charge_shuppin || 0
        },
        re_auc_sold_price: {
          actual: formValue.actual_re_auc_sold_price || 0,
          estimated: formValue.estimated_re_auc_sold_price || 0
        },
        recycling: {
          actual: formValue.actual_recycling || 0,
          estimated: formValue.estimated_recycling || 0
        }
      },

      refunds: {
        recycle_fee_paid: {
          actual: formValue.actual_recycle_fee_paid || 0,
          estimated: formValue.estimated_recycle_fee_paid || 0,
          refundable: formValue.actual_recycle_fee_paid_refundable || false
        },
        automobile_tax: {
          actual: formValue.actual_automobile_tax || 0,
          estimated: formValue.estimated_automobile_tax || 0,
          refundable: formValue.actual_automobile_tax_refundable || false
        },
        consumption_tax: {
          actual: formValue.actual_consumption_tax || 0,
          estimated: formValue.estimated_consumption_tax || 0,
          refundable: formValue.actual_consumption_tax_refundable || false,
          percentage: formValue.actual_consumption_tax_percentage || 10
        },
        car_insurance: {
          actual: formValue.actual_car_insurance || 0,
          estimated: formValue.estimated_car_insurance || 0,
          refundable: formValue.actual_car_insurance_refundable || false
        },
        weight_tax: {
          actual: formValue.actual_weight_tax || 0,
          estimated: formValue.estimated_weight_tax || 0,
          refundable: formValue.actual_weight_tax_refundable || false
        },
        radiation_check_fee: {
          actual: formValue.actual_radiation_check_fee || 0,
          estimated: formValue.estimated_radiation_check_fee || 0,
          refundable: formValue.actual_radiation_check_fee_refundable || false
        },
        meijhenko_deposit: {
          actual: formValue.actual_meijhenko_deposit || 0,
          estimated: formValue.estimated_meijhenko_deposit || 0,
          refundable: formValue.actual_meijhenko_deposit_refundable || false
        }
      },

      local_transport_rikuso: {
        first_transport_charge: {
          actual: formValue.actual_first_transport_charge || 0,
          estimated: formValue.estimated_first_transport_charge || 0
        },
        second_transport_charge: {
          actual: formValue.actual_second_transport_charge || 0,
          estimated: formValue.estimated_second_transport_charge || 0
        },
        third_transport_charge: {
          actual: formValue.actual_third_transport_charge || 0,
          estimated: formValue.estimated_third_transport_charge || 0
        },
        auction_penalty: {
          actual: formValue.actual_auction_penalty || 0,
          estimated: formValue.estimated_auction_penalty || 0
        },
        additional_fee_rikuso_co: {
          actual: formValue.actual_additional_fee_rikuso_co || 0,
          estimated: formValue.estimated_additional_fee_rikuso_co || 0
        },
        fuel_charge: {
          actual: formValue.actual_fuel_charge || 0,
          estimated: formValue.estimated_fuel_charge || 0
        }
      },

      yard_cost: {
        engine_number: {
          actual: formValue.actual_engine_number || 0,
          estimated: formValue.estimated_engine_number || 0
        },
        custom_shipping_charge: {
          actual: formValue.actual_custom_shipping_charge || 0,
          estimated: formValue.estimated_custom_shipping_charge || 0
        },
        yard_cut_penalty: {
          actual: formValue.actual_yard_cut_penalty || 0,
          estimated: formValue.estimated_yard_cut_penalty || 0
        },
        photo_shooting_fee: {
          actual: formValue.actual_photo_shooting_fee || 0,
          estimated: formValue.estimated_photo_shooting_fee || 0
        },
        yard_to_port_yokohama: {
          actual: formValue.actual_yard_to_port_yokohama || 0,
          estimated: formValue.estimated_yard_to_port_yokohama || 0
        },
        cutting_ckd_fees: {
          actual: formValue.actual_cutting_ckd_fees || 0,
          estimated: formValue.estimated_cutting_ckd_fees || 0
        },
        vehicle_repair: {
          actual: formValue.actual_vehicle_repair || 0,
          estimated: formValue.estimated_vehicle_repair || 0
        },
        cleaning_fee: {
          actual: formValue.actual_cleaning_fee || 0,
          estimated: formValue.estimated_cleaning_fee || 0
        },
        inner_cargo_charge: {
          actual: formValue.actual_inner_cargo_charge || 0,
          estimated: formValue.estimated_inner_cargo_charge || 0
        },
        yard_storage_fee: {
          actual: formValue.actual_yard_storage_fee || 0,
          estimated: formValue.estimated_yard_storage_fee || 0
        },
        vanning: {
          actual: formValue.actual_vanning || 0,
          estimated: formValue.estimated_vanning || 0
        },
        parts_disposal_fee: {
          actual: formValue.actual_parts_disposal_fee || 0,
          estimated: formValue.estimated_parts_disposal_fee || 0
        },
        gas_removal_charge: {
          actual: formValue.actual_gas_removal_charge || 0,
          estimated: formValue.estimated_gas_removal_charge || 0
        },
        additional_inspection_fee: {
          actual: formValue.actual_additional_inspection_fee || 0,
          estimated: formValue.estimated_additional_inspection_fee || 0
        },
        mileage_repair: {
          actual: formValue.actual_mileage_repair || 0,
          estimated: formValue.estimated_mileage_repair || 0
        },
        parts: {
          actual: formValue.actual_parts || 0,
          estimated: formValue.estimated_parts || 0
        }
      },

      pre_export_inspection: {
        pre_export_inspection: {
          actual: formValue.actual_pre_export_inspection || 0,
          estimated: formValue.estimated_pre_export_inspection || 0
        },
        jevic: {
          actual: formValue.actual_jevic || 0,
          estimated: formValue.estimated_jevic || 0
        },
        eaa: {
          actual: formValue.actual_eaa || 0,
          estimated: formValue.estimated_eaa || 0
        },
        ati_psi: {
          actual: formValue.actual_ati_psi || 0,
          estimated: formValue.estimated_ati_psi || 0
        },
        mb_insp_cancel_fee: {
          actual: formValue.actual_mb_insp_cancel_fee || 0,
          estimated: formValue.estimated_mb_insp_cancel_fee || 0
        },
        osij: {
          actual: formValue.actual_osij || 0,
          estimated: formValue.estimated_osij || 0
        },
        cat_fee: {
          actual: formValue.actual_cat_fee || 0,
          estimated: formValue.estimated_cat_fee || 0
        },
        heat_treatment_fuj: {
          actual: formValue.actual_heat_treatment_fuj || 0,
          estimated: formValue.estimated_heat_treatment_fuj || 0
        },
        jam: {
          actual: formValue.actual_jam || 0,
          estimated: formValue.estimated_jam || 0
        }
      },

      shipping_costs: {
        bl_charges: {
          actual: formValue.actual_bl_charges || 0,
          estimated: formValue.estimated_bl_charges || 0
        },
        shipping_extra_costs: {
          actual: formValue.actual_shipping_extra_costs || 0,
          estimated: formValue.estimated_shipping_extra_costs || 0
        },
        ocean_freight: {
          actual: formValue.actual_ocean_freight || 0,
          estimated: formValue.estimated_ocean_freight || 0
        },
        cargo_ctn: {
          actual: formValue.actual_cargo_ctn || 0,
          estimated: formValue.estimated_cargo_ctn || 0
        },
        thc: {
          actual: formValue.actual_thc || 0,
          estimated: formValue.estimated_thc || 0
        },
        seal_fee: {
          actual: formValue.actual_seal_fee || 0,
          estimated: formValue.estimated_seal_fee || 0
        },
        doc_fee: {
          actual: formValue.actual_doc_fee || 0,
          estimated: formValue.estimated_doc_fee || 0
        },
        booking_and_commission: {
          actual: formValue.actual_booking_and_commission || 0,
          estimated: formValue.estimated_booking_and_commission || 0
        },
        surrender_fee: {
          actual: formValue.actual_surrender_fee || 0,
          estimated: formValue.estimated_surrender_fee || 0
        },
        bl_docs_fee: {
          actual: formValue.actual_bl_docs_fee || 0,
          estimated: formValue.estimated_bl_docs_fee || 0
        },
        freight_rate: {
          actual: formValue.actual_freight_rate || 0,
          estimated: formValue.estimated_freight_rate || 0
        }
      },

      airmail_charges: {
        dhl_docs_fee: {
          actual: formValue.actual_dhl_docs_fee || 0,
          estimated: formValue.estimated_dhl_docs_fee || 0
        },
        fedex: {
          actual: formValue.actual_fedex || 0,
          estimated: formValue.estimated_fedex || 0
        },
        ems: {
          actual: formValue.actual_ems || 0,
          estimated: formValue.estimated_ems || 0
        }
      },

      isl_internal: {
        sales_commission: {
          actual: formValue.actual_sales_commission || 0,
          estimated: formValue.estimated_sales_commission || 0
        },
        marine_insurance: {
          actual: formValue.actual_marine_insurance || 0,
          estimated: formValue.estimated_marine_insurance || 0
        }
      },

      bank_charges: {
        lc_amendment_fee: {
          actual: formValue.actual_lc_amendment_fee || 0,
          estimated: formValue.estimated_lc_amendment_fee || 0
        },
        collection_fee: {
          actual: formValue.actual_collection_fee || 0,
          estimated: formValue.estimated_collection_fee || 0
        },
        stamp_fee: {
          actual: formValue.actual_stamp_fee || 0,
          estimated: formValue.estimated_stamp_fee || 0
        },
        yen_exchange: {
          actual: formValue.actual_yen_exchange || 0,
          estimated: formValue.estimated_yen_exchange || 0
        },
        issuing_bank_fee: {
          actual: formValue.actual_issuing_bank_fee || 0,
          estimated: formValue.estimated_issuing_bank_fee || 0
        },
        transfer_fee: {
          actual: formValue.actual_transfer_fee || 0,
          estimated: formValue.estimated_transfer_fee || 0
        },
        lc_cable_charge: {
          actual: formValue.actual_lc_cable_charge || 0,
          estimated: formValue.estimated_lc_cable_charge || 0
        }
      },

      extra_charges: {
        extra_parts: {
          actual: formValue.actual_extra_parts || 0,
          estimated: formValue.estimated_extra_parts || 0
        }
      }
    };
  }

  closeModal(): void {
    const modalElement = document.getElementById('kt_modal_vh_price_sheet');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  calculateDifference(actualValue: number, estimatedValue: number): number {
    return (estimatedValue || 0) - (actualValue || 0);
  }

  // Helper method to calculate subtotal from section data
  calculateSectionSubtotal(sectionData: any): { actual: number, estimated: number, difference: number } {
    let actualTotal = 0;
    let estimatedTotal = 0;

    Object.keys(sectionData).forEach(key => {
      if (sectionData[key] && typeof sectionData[key] === 'object' && sectionData[key].actual !== undefined) {
        actualTotal += sectionData[key].actual || 0;
        estimatedTotal += sectionData[key].estimated || 0;
      }
    });

    return {
      actual: actualTotal,
      estimated: estimatedTotal,
      difference: estimatedTotal - actualTotal
    };
  }

  // Method to get complete data with calculated subtotals (if needed for API)
  getCompleteDataWithSubtotals(): any {
    const baseData = this.preparePriceSheetData();

    // Calculate subtotals for each section
    const subtotals = {
      description_subtotal: this.calculateSectionSubtotal(baseData.description),
      refunds_subtotal: this.calculateSectionSubtotal(baseData.refunds),
      local_transport_subtotal: this.calculateSectionSubtotal(baseData.local_transport_rikuso),
      yard_cost_subtotal: this.calculateSectionSubtotal(baseData.yard_cost),
      pre_export_inspection_subtotal: this.calculateSectionSubtotal(baseData.pre_export_inspection),
      shipping_costs_subtotal: this.calculateSectionSubtotal(baseData.shipping_costs),
      airmail_charges_subtotal: this.calculateSectionSubtotal(baseData.airmail_charges),
      isl_internal_subtotal: this.calculateSectionSubtotal(baseData.isl_internal),
      bank_charges_subtotal: this.calculateSectionSubtotal(baseData.bank_charges),
      extra_charges_subtotal: this.calculateSectionSubtotal(baseData.extra_charges)
    };

    // Calculate grand totals
    const totalCostActual = Object.values(subtotals).reduce((sum: number, subtotal: any) => sum + subtotal.actual, 0);
    const totalCostEstimated = Object.values(subtotals).reduce((sum: number, subtotal: any) => sum + subtotal.estimated, 0);

    const grandTotals = {
      total_cost: {
        actual: totalCostActual,
        estimated: totalCostEstimated,
        difference: totalCostEstimated - totalCostActual
      },
      income: { actual: 0, estimated: 0, difference: 0 }, // You can add income calculation logic here
      refunds: subtotals.refunds_subtotal,
      loss: {
        actual: totalCostActual - 0 - subtotals.refunds_subtotal.actual, // totalCost - income - refunds
        estimated: totalCostEstimated - 0 - subtotals.refunds_subtotal.estimated,
        difference: (totalCostEstimated - 0 - subtotals.refunds_subtotal.estimated) - (totalCostActual - 0 - subtotals.refunds_subtotal.actual)
      }
    };

    return {
      ...baseData,
      subtotals,
      grand_totals: grandTotals
    };
  }

  getVehicleDisplayName(): string {
    if (!this.selectedVehicle) return '';
    return `${this.selectedVehicle.year || ''} ${this.selectedVehicle.make || ''} ${this.selectedVehicle.model || ''}`.trim();
  }

  private setupFormValueChanges(): void {
    // Initial calculation
    setTimeout(() => {
      this.calculateAuctionPriceTax();
      this.calculateBidChargeTax();
      this.calculateAllSubtotals();
      this.calculateGrandTotals();
    }, 100);

    // Clear existing form value change subscription to prevent duplicates
    if (this.formValueChangeSubscription) {
      this.formValueChangeSubscription.unsubscribe();
    }

    this.formValueChangeSubscription = this.priceSheetForm.valueChanges.subscribe(() => {
      this.calculateAuctionPriceTax();
      this.calculateBidChargeTax();
      this.calculateAllSubtotals();
      this.calculateGrandTotals();
    });

    this.subscriptions.push(this.formValueChangeSubscription);

    // Add specific listeners for tax-related fields to trigger immediate recalculation
    this.setupTaxCalculationListeners();
  }

  private setupTaxCalculationListeners(): void {
    // Listen to auction price changes
    this.subscriptions.push(
      this.priceSheetForm.get('actual_auction_price')?.valueChanges.subscribe(() => {
        this.calculateAuctionPriceTax();
      }) as any
    );
    this.subscriptions.push(
      this.priceSheetForm.get('estimated_auction_price')?.valueChanges.subscribe(() => {
        this.calculateAuctionPriceTax();
      }) as any
    );
    this.subscriptions.push(
      this.priceSheetForm.get('actual_auc_price_tax_percentage')?.valueChanges.subscribe(() => {
        this.calculateAuctionPriceTax();
      }) as any
    );
    this.subscriptions.push(
      this.priceSheetForm.get('estimated_auc_price_tax_percentage')?.valueChanges.subscribe(() => {
        this.calculateAuctionPriceTax();
      }) as any
    );

    // Listen to bid charge changes
    this.subscriptions.push(
      this.priceSheetForm.get('actual_bid_charge')?.valueChanges.subscribe(() => {
        this.calculateBidChargeTax();
      }) as any
    );
    this.subscriptions.push(
      this.priceSheetForm.get('estimated_bid_charge')?.valueChanges.subscribe(() => {
        this.calculateBidChargeTax();
      }) as any
    );
    this.subscriptions.push(
      this.priceSheetForm.get('actual_bid_charge_tax_percentage')?.valueChanges.subscribe(() => {
        this.calculateBidChargeTax();
      }) as any
    );
    this.subscriptions.push(
      this.priceSheetForm.get('estimated_bid_charge_tax_percentage')?.valueChanges.subscribe(() => {
        this.calculateBidChargeTax();
      }) as any
    );
  }

  private calculateAllSubtotals(): void {
    this.calculateDescriptionSubtotal();
    this.calculateAuctionChargesSubtotal();
    this.calculateRefundsSubtotal();
    this.calculateLocalTransportSubtotal();
    this.calculateYardCostSubtotal();
    this.calculatePreExportInspectionSubtotal();
    this.calculateShippingCostsSubtotal();
    this.calculateAirmailChargesSubtotal();
    this.calculateIslInternalSubtotal();
    this.calculateBankChargesSubtotal();
    this.calculateExtraChargesSubtotal();

    // Trigger change detection
    this.cdr.detectChanges();
  }

  private calculateDescriptionSubtotal(): void {
    const form = this.priceSheetForm.value;

    // Description section now includes all 4 separate fields
    this.descriptionSubtotal.actual =
      (form.actual_selling_price_usd || 0) +
      (form.actual_rate || 0) +
      (form.actual_actual_cost_jpy || 0) +
      (form.actual_sales_cost || 0);

    this.descriptionSubtotal.estimated =
      (form.estimated_selling_price_usd || 0) +
      (form.estimated_rate || 0) +
      (form.estimated_actual_cost_jpy || 0) +
      (form.estimated_sales_cost || 0);

    this.descriptionSubtotal.difference = this.descriptionSubtotal.estimated - this.descriptionSubtotal.actual;
  }

  private calculateAuctionChargesSubtotal(): void {
    const form = this.priceSheetForm.value;

    // Auction charges section includes all the previous description items except selling price and rate
    this.auctionChargesSubtotal.actual =
      (form.actual_auction_charges || 0) +
      (form.actual_auction_price || 0) +
      (form.actual_auction_price_tax || 0) +
      (form.actual_bid_charge || 0) +
      (form.actual_bid_price_tax || 0) +
      (form.actual_sold_charge_seiyaku || 0) +
      (form.actual_selling_charge_shuppin || 0) +
      (form.actual_re_auc_sold_price || 0) +
      (form.actual_recycling || 0);

    this.auctionChargesSubtotal.estimated =
      (form.estimated_auction_charges || 0) +
      (form.estimated_auction_price || 0) +
      (form.estimated_auction_price_tax || 0) +
      (form.estimated_bid_charge || 0) +
      (form.estimated_bid_price_tax || 0) +
      (form.estimated_sold_charge_seiyaku || 0) +
      (form.estimated_selling_charge_shuppin || 0) +
      (form.estimated_re_auc_sold_price || 0) +
      (form.estimated_recycling || 0);

    this.auctionChargesSubtotal.difference = this.auctionChargesSubtotal.estimated - this.auctionChargesSubtotal.actual;
  }

  private calculateRefundsSubtotal(): void {
    const form = this.priceSheetForm.value;

    this.refundsSubtotal.actual =
      (form.actual_recycle_fee_paid || 0) +
      (form.actual_automobile_tax || 0) +
      (form.actual_consumption_tax || 0) +
      (form.actual_car_insurance || 0) +
      (form.actual_weight_tax || 0) +
      (form.actual_radiation_check_fee || 0) +
      (form.actual_meijhenko_deposit || 0);

    this.refundsSubtotal.estimated =
      (form.estimated_recycle_fee_paid || 0) +
      (form.estimated_automobile_tax || 0) +
      (form.estimated_consumption_tax || 0) +
      (form.estimated_car_insurance || 0) +
      (form.estimated_weight_tax || 0) +
      (form.estimated_radiation_check_fee || 0) +
      (form.estimated_meijhenko_deposit || 0);

    this.refundsSubtotal.difference = this.refundsSubtotal.estimated - this.refundsSubtotal.actual;
  }

  private calculateLocalTransportSubtotal(): void {
    const form = this.priceSheetForm.value;

    this.localTransportSubtotal.actual =
      (form.actual_first_transport_charge || 0) +
      (form.actual_second_transport_charge || 0) +
      (form.actual_third_transport_charge || 0) +
      (form.actual_auction_penalty || 0) +
      (form.actual_additional_fee_rikuso_co || 0) +
      (form.actual_fuel_charge || 0);

    this.localTransportSubtotal.estimated =
      (form.estimated_first_transport_charge || 0) +
      (form.estimated_second_transport_charge || 0) +
      (form.estimated_third_transport_charge || 0) +
      (form.estimated_auction_penalty || 0) +
      (form.estimated_additional_fee_rikuso_co || 0) +
      (form.estimated_fuel_charge || 0);

    this.localTransportSubtotal.difference = this.localTransportSubtotal.estimated - this.localTransportSubtotal.actual;
  }

  private calculateYardCostSubtotal(): void {
    const form = this.priceSheetForm.value;

    this.yardCostSubtotal.actual =
      (form.actual_engine_number || 0) +
      (form.actual_custom_shipping_charge || 0) +
      (form.actual_yard_cut_penalty || 0) +
      (form.actual_photo_shooting_fee || 0) +
      (form.actual_yard_to_port_yokohama || 0) +
      (form.actual_cutting_ckd_fees || 0) +
      (form.actual_vehicle_repair || 0) +
      (form.actual_cleaning_fee || 0) +
      (form.actual_inner_cargo_charge || 0) +
      (form.actual_yard_storage_fee || 0) +
      (form.actual_vanning || 0) +
      (form.actual_parts_disposal_fee || 0) +
      (form.actual_gas_removal_charge || 0) +
      (form.actual_additional_inspection_fee || 0) +
      (form.actual_mileage_repair || 0) +
      (form.actual_parts || 0);

    this.yardCostSubtotal.estimated =
      (form.estimated_engine_number || 0) +
      (form.estimated_custom_shipping_charge || 0) +
      (form.estimated_yard_cut_penalty || 0) +
      (form.estimated_photo_shooting_fee || 0) +
      (form.estimated_yard_to_port_yokohama || 0) +
      (form.estimated_cutting_ckd_fees || 0) +
      (form.estimated_vehicle_repair || 0) +
      (form.estimated_cleaning_fee || 0) +
      (form.estimated_inner_cargo_charge || 0) +
      (form.estimated_yard_storage_fee || 0) +
      (form.estimated_vanning || 0) +
      (form.estimated_parts_disposal_fee || 0) +
      (form.estimated_gas_removal_charge || 0) +
      (form.estimated_additional_inspection_fee || 0) +
      (form.estimated_mileage_repair || 0) +
      (form.estimated_parts || 0);

    this.yardCostSubtotal.difference = this.yardCostSubtotal.estimated - this.yardCostSubtotal.actual;
  }

  private calculatePreExportInspectionSubtotal(): void {
    const form = this.priceSheetForm.value;

    this.preExportInspectionSubtotal.actual =
      (form.actual_pre_export_inspection || 0) +
      (form.actual_jevic || 0) +
      (form.actual_eaa || 0) +
      (form.actual_ati_psi || 0) +
      (form.actual_mb_insp_cancel_fee || 0) +
      (form.actual_osij || 0) +
      (form.actual_cat_fee || 0) +
      (form.actual_heat_treatment_fuj || 0) +
      (form.actual_jam || 0);

    this.preExportInspectionSubtotal.estimated =
      (form.estimated_pre_export_inspection || 0) +
      (form.estimated_jevic || 0) +
      (form.estimated_eaa || 0) +
      (form.estimated_ati_psi || 0) +
      (form.estimated_mb_insp_cancel_fee || 0) +
      (form.estimated_osij || 0) +
      (form.estimated_cat_fee || 0) +
      (form.estimated_heat_treatment_fuj || 0) +
      (form.estimated_jam || 0);

    this.preExportInspectionSubtotal.difference = this.preExportInspectionSubtotal.estimated - this.preExportInspectionSubtotal.actual;
  }

  private calculateShippingCostsSubtotal(): void {
    const form = this.priceSheetForm.value;

    this.shippingCostsSubtotal.actual =
      (form.actual_bl_charges || 0) +
      (form.actual_shipping_extra_costs || 0) +
      (form.actual_ocean_freight || 0) +
      (form.actual_cargo_ctn || 0) +
      (form.actual_thc || 0) +
      (form.actual_seal_fee || 0) +
      (form.actual_doc_fee || 0) +
      (form.actual_booking_and_commission || 0) +
      (form.actual_surrender_fee || 0) +
      (form.actual_bl_docs_fee || 0) +
      (form.actual_freight_rate || 0);

    this.shippingCostsSubtotal.estimated =
      (form.estimated_bl_charges || 0) +
      (form.estimated_shipping_extra_costs || 0) +
      (form.estimated_ocean_freight || 0) +
      (form.estimated_cargo_ctn || 0) +
      (form.estimated_thc || 0) +
      (form.estimated_seal_fee || 0) +
      (form.estimated_doc_fee || 0) +
      (form.estimated_booking_and_commission || 0) +
      (form.estimated_surrender_fee || 0) +
      (form.estimated_bl_docs_fee || 0) +
      (form.estimated_freight_rate || 0);

    this.shippingCostsSubtotal.difference = this.shippingCostsSubtotal.estimated - this.shippingCostsSubtotal.actual;
  }

  private calculateAirmailChargesSubtotal(): void {
    const form = this.priceSheetForm.value;

    this.airmailChargesSubtotal.actual =
      (form.actual_dhl_docs_fee || 0) +
      (form.actual_fedex || 0) +
      (form.actual_ems || 0);

    this.airmailChargesSubtotal.estimated =
      (form.estimated_dhl_docs_fee || 0) +
      (form.estimated_fedex || 0) +
      (form.estimated_ems || 0);

    this.airmailChargesSubtotal.difference = this.airmailChargesSubtotal.estimated - this.airmailChargesSubtotal.actual;
  }

  private calculateIslInternalSubtotal(): void {
    const form = this.priceSheetForm.value;

    this.islInternalSubtotal.actual =
      (form.actual_sales_commission || 0) +
      (form.actual_marine_insurance || 0);

    this.islInternalSubtotal.estimated =
      (form.estimated_sales_commission || 0) +
      (form.estimated_marine_insurance || 0);

    this.islInternalSubtotal.difference = this.islInternalSubtotal.estimated - this.islInternalSubtotal.actual;
  }

  private calculateBankChargesSubtotal(): void {
    const form = this.priceSheetForm.value;

    this.bankChargesSubtotal.actual =
      (form.actual_lc_amendment_fee || 0) +
      (form.actual_collection_fee || 0) +
      (form.actual_stamp_fee || 0) +
      (form.actual_yen_exchange || 0) +
      (form.actual_issuing_bank_fee || 0) +
      (form.actual_transfer_fee || 0) +
      (form.actual_lc_cable_charge || 0);

    this.bankChargesSubtotal.estimated =
      (form.estimated_lc_amendment_fee || 0) +
      (form.estimated_collection_fee || 0) +
      (form.estimated_stamp_fee || 0) +
      (form.estimated_yen_exchange || 0) +
      (form.estimated_issuing_bank_fee || 0) +
      (form.estimated_transfer_fee || 0) +
      (form.estimated_lc_cable_charge || 0);

    this.bankChargesSubtotal.difference = this.bankChargesSubtotal.estimated - this.bankChargesSubtotal.actual;
  }

  private calculateExtraChargesSubtotal(): void {
    const form = this.priceSheetForm.value;

    this.extraChargesSubtotal.actual = (form.actual_extra_parts || 0);
    this.extraChargesSubtotal.estimated = (form.estimated_extra_parts || 0);
    this.extraChargesSubtotal.difference = this.extraChargesSubtotal.estimated - this.extraChargesSubtotal.actual;
  }

  // Automatic Tax Calculation Methods
  private calculateAuctionPriceTax(): void {
    const actualAuctionPrice = this.priceSheetForm.get('actual_auction_price')?.value || 0;
    const estimatedAuctionPrice = this.priceSheetForm.get('estimated_auction_price')?.value || 0;
    const actualPercentage = this.priceSheetForm.get('actual_auc_price_tax_percentage')?.value || 0;
    const estimatedPercentage = this.priceSheetForm.get('estimated_auc_price_tax_percentage')?.value || 0;

    const actualTax = (actualAuctionPrice * actualPercentage) / 100;
    const estimatedTax = (estimatedAuctionPrice * estimatedPercentage) / 100;

    // Update the form values without emitting events to avoid infinite loops
    this.priceSheetForm.patchValue({
      actual_auction_price_tax: actualTax,
      estimated_auction_price_tax: estimatedTax
    }, { emitEvent: false });
  }

  private calculateBidChargeTax(): void {
    const actualBidCharge = this.priceSheetForm.get('actual_bid_charge')?.value || 0;
    const estimatedBidCharge = this.priceSheetForm.get('estimated_bid_charge')?.value || 0;
    const actualPercentage = this.priceSheetForm.get('actual_bid_charge_tax_percentage')?.value || 0;
    const estimatedPercentage = this.priceSheetForm.get('estimated_bid_charge_tax_percentage')?.value || 0;

    const actualTax = (actualBidCharge * actualPercentage) / 100;
    const estimatedTax = (estimatedBidCharge * estimatedPercentage) / 100;

    // Update the form values without emitting events to avoid infinite loops
    this.priceSheetForm.patchValue({
      actual_bid_price_tax: actualTax,
      estimated_bid_price_tax: estimatedTax
    }, { emitEvent: false });
  }

  private calculateGrandTotals(): void {
    // Calculate total cost (sum of all subtotals except refunds and description section)
    // Description section (Actual Cost(JPY), Selling Price(USD), Rate) is excluded from total cost
    this.grandTotals.totalCost.actual =
      this.auctionChargesSubtotal.actual +
      this.localTransportSubtotal.actual +
      this.yardCostSubtotal.actual +
      this.preExportInspectionSubtotal.actual +
      this.shippingCostsSubtotal.actual +
      this.airmailChargesSubtotal.actual +
      this.islInternalSubtotal.actual +
      this.bankChargesSubtotal.actual +
      this.extraChargesSubtotal.actual;

    this.grandTotals.totalCost.estimated =
      this.auctionChargesSubtotal.estimated +
      this.localTransportSubtotal.estimated +
      this.yardCostSubtotal.estimated +
      this.preExportInspectionSubtotal.estimated +
      this.shippingCostsSubtotal.estimated +
      this.airmailChargesSubtotal.estimated +
      this.islInternalSubtotal.estimated +
      this.bankChargesSubtotal.estimated +
      this.extraChargesSubtotal.estimated;

    this.grandTotals.totalCost.difference = this.grandTotals.totalCost.estimated - this.grandTotals.totalCost.actual;

    // Refunds are separate
    this.grandTotals.refunds.actual = this.refundsSubtotal.actual;
    this.grandTotals.refunds.estimated = this.refundsSubtotal.estimated;
    this.grandTotals.refunds.difference = this.refundsSubtotal.difference;

    // Income shows the JPY values from Description section (Selling Price + Rate)
    this.grandTotals.income.actual = this.descriptionSubtotal.actual;
    this.grandTotals.income.estimated = this.descriptionSubtotal.estimated;
    this.grandTotals.income.difference = this.descriptionSubtotal.difference;

    // Loss = Total Cost - Income - Refunds
    this.grandTotals.loss.actual = this.grandTotals.totalCost.actual - this.grandTotals.income.actual - this.grandTotals.refunds.actual;
    this.grandTotals.loss.estimated = this.grandTotals.totalCost.estimated - this.grandTotals.income.estimated - this.grandTotals.refunds.estimated;
    this.grandTotals.loss.difference = this.grandTotals.loss.estimated - this.grandTotals.loss.actual;
  }
}
